import {useAuth} from '@apps-common/auth-react';
import CloseIcon from '@mui/icons-material/Close';
import {
  AppBar,
  Box,
  Container,
  Dialog,
  DialogContent,
  Grid,
  IconButton,
  Link,
  Tooltip,
  Typography,
} from '@mui/material';
import PropTypes from 'prop-types';
import React from 'react';
import {Trans, useTranslation} from 'react-i18next';

import MetadataTable from './table.jsx';
import {trackEvent} from '@explorer/analytics.js';
import CopyButton from '@explorer/components/ui/copy-button';
import ThumbnailImage from '@explorer/components/ui/thumbnail-image';
import {getUrl as getThumbUrl} from '@explorer/util/thumbs';

const MetadataModal = props => {
  const {t} = useTranslation();
  const auth = useAuth();

  return (
    <Dialog
      fullScreen
      open={props.open}
      onClose={props.onClose}
      sx={{
        '& .MuiDialog-paper': {
          borderRadius: 0,
          boxShadow: 'none',
          backgroundImage: 'none',
          backgroundColor: 'rgba(22, 24, 34, 0.9)',
        },
      }}
      data-cy="metadata-overlay"
      className="pl-mode-dark"
    >
      <React.Fragment>
        <AppBar position="sticky" color="transparent" elevation={0}>
          <Box
            sx={{
              minHeight: 48,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <Box
              sx={{
                position: 'absolute',
                top: 0.5,
                right: 0.5,
              }}
            >
              <Tooltip
                title={t('metadataModal.closeButtonTooltip')}
                placement="left"
              >
                <IconButton onClick={props.onClose}>
                  <CloseIcon />
                </IconButton>
              </Tooltip>
            </Box>
            <Typography color="textPrimary" align="center">
              <Trans
                i18nKey="metadataModal.messageExit"
                components={{
                  exitLink: <Link underline="always" onClick={props.onClose} />,
                }}
              />
            </Typography>
          </Box>
        </AppBar>

        <DialogContent
          sx={{
            display: 'flex',
            alignItems: 'center',
            height: '100%',
          }}
        >
          <Container disableGutters>
            <Grid
              container
              spacing={4}
              sx={{
                justifyContent: 'center',
              }}
            >
              <Grid
                item
                xs={6}
                sm={5}
                container
                sx={{
                  alignItems: 'center',
                }}
              >
                <Box
                  sx={{
                    my: 1,
                    width: '100%',
                  }}
                >
                  <Typography
                    color="textPrimary"
                    sx={{
                      display: 'inline',
                      marginRight: 0.5,
                    }}
                  >
                    {t('common:date.med', {
                      date: props.item.properties.acquired,
                    })}
                  </Typography>
                  <Typography
                    variant="caption"
                    sx={{
                      display: 'inline',
                    }}
                  >
                    {t('common:time.long', {
                      date: props.item.properties.acquired,
                    })}
                  </Typography>
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                    }}
                  >
                    <Typography
                      color="textPrimary"
                      noWrap
                      sx={{marginRight: 1}}
                    >
                      {props.item.properties.item_id || props.item.id}
                    </Typography>
                    <CopyButton
                      textToCopy={
                        props.item.properties.item_id || props.item.id
                      }
                      onCopy={() =>
                        trackEvent('Metadata Modal: Copied scene id', {
                          sceneId: props.item.id,
                          hostedData: Boolean(
                            props.item.properties.hosted_metadata
                          ),
                        })
                      }
                      title={t('metadataModal.buttonCopyScene')}
                    />
                  </Box>
                </Box>
                <MetadataTable item={props.item} />
              </Grid>
              <Grid
                item
                xs={6}
                sm={7}
                container
                sx={{
                  justifyContent: 'center',
                  alignItems: 'center',
                }}
              >
                <ThumbnailImage
                  src={getThumbUrl({
                    item: props.item,
                    size: 512,
                    token: auth.user?.accessToken,
                  })}
                  maxWidth={512}
                  maxHeight={512}
                  errorSize="large"
                />
              </Grid>
            </Grid>
          </Container>
        </DialogContent>
      </React.Fragment>
    </Dialog>
  );
};

MetadataModal.propTypes = {
  item: PropTypes.object.isRequired,
  onClose: PropTypes.func,
  open: PropTypes.bool,
};

export default MetadataModal;
